import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Copy, Trash2, Plus } from "lucide-react";
import { API, BACKEND_URL } from "@/lib/api";

export const ApiKeysCard = ({ hasFeature }) => {
  const enabled = hasFeature("multi_user");
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("");
  const [newKey, setNewKey] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await axios.get(`${API}/apikeys`); setKeys(r.data || []); }
    catch { setKeys([]); }
  };
  useEffect(() => { if (enabled) load(); }, [enabled]);

  const copy = (text, msg) => navigator.clipboard.writeText(text).then(() => toast.success(msg), () => toast.error("Kopieren fehlgeschlagen"));

  const create = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/apikeys`, { name: name.trim() || "API-Schlüssel" });
      setNewKey(r.data);
      setName("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erstellen fehlgeschlagen"); }
    finally { setBusy(false); }
  };

  const revoke = async (k) => {
    if (!window.confirm(`Schlüssel „${k.name}" (${k.prefix}…) widerrufen? Externe Zugriffe damit funktionieren sofort nicht mehr.`)) return;
    try { await axios.delete(`${API}/apikeys/${k.id}`); toast.success("Schlüssel widerrufen"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Fehlgeschlagen"); }
  };

  return (
    <Card className="p-6 bg-white border-[var(--brand-border)] lg:col-span-2" data-testid="settings-apikeys-card">
      <h2 className="font-[Playfair_Display] text-xl font-semibold text-[var(--brand-ink)] mb-1 flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-[var(--brand-primary)]" /> API-Zugriff (REST)
      </h2>
      <p className="text-sm text-[var(--brand-muted)] mb-4">
        Erstelle API-Schlüssel, um deine Daten (Kunden, Dokumente, Deals, Leads) <b>lesend</b> in andere Systeme zu integrieren – z. B. BI-Tools, ERP oder eigene Skripte.
      </p>

      {!enabled ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="apikeys-upsell">
          API-Zugriff ist im <b>Business-Tarif</b> enthalten.
        </div>
      ) : (
        <>
          <div className="flex items-end gap-3 mb-4">
            <div className="w-64">
              <Label>Bezeichnung</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Power-BI-Anbindung" data-testid="apikey-name" />
            </div>
            <Button onClick={create} disabled={busy} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="apikey-create-btn">
              <Plus className="w-4 h-4 mr-1" /> Schlüssel erstellen
            </Button>
          </div>

          {newKey && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 mb-4" data-testid="apikey-new-box">
              <div className="text-sm font-semibold text-emerald-800 mb-1">Neuer Schlüssel „{newKey.name}" – wird nur EINMAL angezeigt:</div>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white border border-emerald-200 rounded px-2 py-1.5 break-all flex-1" data-testid="apikey-new-value">{newKey.key}</code>
                <Button size="sm" variant="outline" onClick={() => copy(newKey.key, "API-Schlüssel kopiert")} className="border-emerald-300 shrink-0" data-testid="apikey-copy-btn">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <button onClick={() => setNewKey(null)} className="text-[11px] text-emerald-700 hover:underline mt-2" data-testid="apikey-new-dismiss">Gespeichert, ausblenden</button>
            </div>
          )}

          {keys.length > 0 && (
            <div className="space-y-1.5 mb-4" data-testid="apikeys-list">
              {keys.map((k) => (
                <div key={k.id} className="flex items-center gap-3 rounded-lg border border-[var(--brand-border-light)] px-3 py-2" data-testid={`apikey-row-${k.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[var(--brand-ink)] truncate">{k.name}</div>
                    <div className="text-[11px] text-[var(--brand-muted)] font-mono">{k.prefix}… · erstellt {new Date(k.created_at).toLocaleDateString("de-DE")}{k.last_used_at ? ` · zuletzt genutzt ${new Date(k.last_used_at).toLocaleDateString("de-DE")}` : " · noch nie genutzt"}</div>
                  </div>
                  <button onClick={() => revoke(k)} className="text-red-500 hover:text-red-700 shrink-0" title="Widerrufen" data-testid={`apikey-revoke-${k.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-lg bg-[var(--brand-bg)] border border-[var(--brand-border-light)] p-3">
            <div className="text-[11px] uppercase tracking-wider text-[var(--brand-muted)] font-semibold mb-1">Beispiel</div>
            <code className="text-[11px] text-[var(--brand-ink)] break-all block">
              curl -H "X-API-Key: DEIN_SCHLÜSSEL" {BACKEND_URL}/api/v1/customers
            </code>
            <div className="text-[11px] text-[var(--brand-muted)] mt-1.5">
              Endpunkte: <code>/api/v1/customers</code> · <code>/api/v1/documents?typ=rechnung</code> · <code>/api/v1/deals</code> · <code>/api/v1/leads</code> (alle read-only)
            </div>
          </div>
        </>
      )}
    </Card>
  );
};
